import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { UploadCloud, Clipboard, Scissors, Trash2, Loader2, FileUp, AlertTriangle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import LoadingProgress from '@/components/LoadingProgress';
import { useFileImport } from '@/hooks/useFileImport';


const FIELD_NAMES = ['data', 'bandeira', 'tipotransacao', 'parcelas', 'valorcompra', 'codigoautorizacao', 'codigopedido'];
const LABELS = ['Data da Venda', 'Bandeira', 'Tipo', 'Parcelas', 'Valor', 'Cód. Autorização', 'Cód. do Pedido'];
const createEmptyRow = (id) => ({ id, data: '', bandeira: '', tipotransacao: '', parcelas: '', valorcompra: '', codigoautorizacao: '', codigopedido: '' });



const PasteSalesForm = ({
  isOpen,
  onClose,
  onSave,
  operators,
  formState,
  setFormState,
}) => {
  const { toast } = useToast();
  const { step, selectedOperator, gridData } = formState;
  const [contextMenu, setContextMenu] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState(0);
  const [currentNegativeIndex, setCurrentNegativeIndex] = useState(-1);
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);

  // Hook para importação de arquivos
  const { importFile, isImporting } = useFileImport();

  const hasNegativeValues = React.useMemo(() => {
    return gridData.some(row => String(row.valorcompra || '').trim().startsWith('-'));
  }, [gridData]);

  const scrollToNextNegative = () => {
    const negativeIndices = gridData.reduce((acc, row, idx) => {
      if (String(row.valorcompra || '').trim().startsWith('-')) acc.push(idx);
      return acc;
    }, []);

    if (negativeIndices.length === 0) return;

    let nextIdx = negativeIndices.find(idx => idx > currentNegativeIndex);
    if (nextIdx === undefined) nextIdx = negativeIndices[0];

    setCurrentNegativeIndex(nextIdx);

    // col='4' corresponds to 'valorcompra'
    const input = gridRef.current?.querySelector(`[data-row='${nextIdx}'][data-col='4']`);
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleGridChange = (rowIndex, colIndex, value) => {
    setFormState(prev => {
      const newGridData = [...prev.gridData];
      newGridData[rowIndex][FIELD_NAMES[colIndex]] = value;
      return { ...prev, gridData: newGridData };
    });
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    let nextRow = rowIndex;
    let nextCol = colIndex;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        nextRow = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        nextRow = Math.min(gridData.length - 1, rowIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        nextCol = Math.max(0, colIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        nextCol = Math.min(FIELD_NAMES.length - 1, colIndex + 1);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          if (colIndex > 0) nextCol = colIndex - 1;
          else if (rowIndex > 0) {
            nextRow = rowIndex - 1;
            nextCol = FIELD_NAMES.length - 1;
          }
        } else {
          if (colIndex < FIELD_NAMES.length - 1) nextCol = colIndex + 1;
          else if (rowIndex < gridData.length - 1) {
            nextRow = rowIndex + 1;
            nextCol = 0;
          } else {
            setFormState(prev => ({ ...prev, gridData: [...prev.gridData, createEmptyRow(Date.now())] }));
            nextRow = rowIndex + 1;
            nextCol = 0;
          }
        }
        break;
      default:
        return;
    }

    const nextInput = gridRef.current?.querySelector(`[data-row='${nextRow}'][data-col='${nextCol}']`);
    if (nextInput) {
      nextInput.focus();
    }
  };


  const handlePaste = (e, startRow, startCol) => {
    e.preventDefault();
    const pasteText = e.clipboardData.getData('text');
    const pastedRows = pasteText.split('\n').map(row => row.split('\t'));

    setFormState(prev => {
      let newGridData = [...prev.gridData];

      pastedRows.forEach((rowData, r_offset) => {
        const currentRowIndex = startRow + r_offset;

        if (currentRowIndex >= newGridData.length) {
          const rowsToAdd = currentRowIndex - newGridData.length + 1;
          for (let i = 0; i < rowsToAdd; i++) {
            newGridData.push(createEmptyRow(Date.now() + startRow + r_offset + i));
          }
        }

        rowData.forEach((cellData, c_offset) => {
          const currentColIndex = startCol + c_offset;
          if (currentColIndex >= FIELD_NAMES.length) return;

          newGridData[currentRowIndex][FIELD_NAMES[currentColIndex]] = cellData.trim();
        });
      });

      const lastDataRow = newGridData.map(r => Object.values(r).join('')).lastIndexOf(val => val.trim() !== '');
      const requiredRows = Math.max(20, lastDataRow + 21);
      if (newGridData.length < requiredRows) {
        const rowsToAdd = requiredRows - newGridData.length;
        for (let i = 0; i < rowsToAdd; i++) {
          newGridData.push(createEmptyRow(Date.now() + newGridData.length + i));
        }
      }

      return { ...prev, gridData: newGridData };
    });
  };

  /**
   * Mapeia dados importados do microserviço para o formato do grid
   */
  const mapImportedDataToGrid = (importedData) => {
    return importedData.map((item, index) => ({
      id: Date.now() + index,
      data: item.data_venda || item.data_pagamento || '',
      bandeira: item.bandeira || '',
      tipotransacao: item.tipo || '',
      parcelas: item.parcelas || '',
      valorcompra: item.valor || '',
      codigoautorizacao: item.codigo_autorizacao || '',
      codigopedido: item.codigo_pedido || ''
    }));
  };

  /**
   * Handler para importação de arquivo
   */
  const handleFileImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await importFile(file, 'vendas');

      // ✅ VALIDAÇÃO: Verificar se o arquivo é do tipo correto
      if (result.metadata.fileType !== 'vendas') {
        toast({
          title: "Arquivo incompatível",
          description: "Este é um arquivo de recebimentos. Por favor, importe um arquivo de vendas (contendo 'Data de venda' e 'Valor bruto da parcela').",
          variant: "destructive",
        });
        return;
      }

      // Mapear dados para formato do grid
      const mappedData = mapImportedDataToGrid(result.data);

      // Adicionar linhas vazias extras para edição
      const emptyRowsToAdd = Array(20).fill(null).map((_, i) => createEmptyRow(Date.now() + mappedData.length + i));

      // Atualizar grid com dados importados
      setFormState(prev => ({
        ...prev,
        gridData: [...mappedData, ...emptyRowsToAdd]
      }));

      // Mostrar mensagem de sucesso
      toast({
        title: "Arquivo importado com sucesso!",
        description: `${result.data.length} vendas foram carregadas.`,
        variant: "default",
      });

    } catch (error) {
      toast({
        title: "Erro ao importar arquivo",
        description: error.message || "Não foi possível processar o arquivo. Verifique se o microserviço está em execução.",
        variant: "destructive",
      });
    } finally {
      // Limpar input para permitir reimportação do mesmo arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Funções de agrupamento removidas - modelo 1:1 mantém cada parcela como venda individual

  const handleSave = async () => {
    const salesToSave = gridData
      .map((row, index) => ({ ...row, originalIndex: index }))
      .filter(row => row.data && (row.valorcompra || row.valorcompra === '0'));

    if (salesToSave.length === 0) {
      toast({ title: "Nenhuma venda para salvar", description: "Preencha pelo menos a data e o valor de uma venda.", variant: "destructive" });
      return;
    }

    // A detecção de duplicatas agora é feita de forma mais robusta no useDataMutation
    // que verifica duplicatas no banco de dados e entre todas as vendas do lote
    flushSync(() => {
      setIsSaving(true);
      setSaveProgress(0);
    });

    // Simular progresso inicial
    setSaveProgress(10);

    await onSave(salesToSave, selectedOperator, {
      onProgress: (progress) => {
        setSaveProgress(progress);
      },
      onComplete: (remainingRows) => {
        setSaveProgress(100);
        setTimeout(() => {
          setIsSaving(false);
          setSaveProgress(0);
        }, 500);
        if (remainingRows && remainingRows.length > 0) {
          const updatedGrid = [...remainingRows];
          const emptyRowsToAdd = Array(Math.max(0, 20 - updatedGrid.length)).fill(null).map((_, i) => createEmptyRow(Date.now() + i));
          setFormState(prev => ({ ...prev, gridData: [...updatedGrid, ...emptyRowsToAdd] }));
        } else {
          onClose();
        }
      }
    });
  };

  const getOperatorName = () => {
    return operators.find(op => op.id === selectedOperator)?.name || '';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const menuElement = document.getElementById('context-menu-sales');
      if (gridRef.current && !gridRef.current.contains(event.target) && (!menuElement || !menuElement.contains(event.target))) {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleContextMenu = (e, type, index) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, type, index });
  };

  const handleContextAction = (action) => {
    if (!contextMenu) return;
    const { type, index } = contextMenu;

    if (type === 'row') {
      const rowData = gridData[index];
      const rowText = FIELD_NAMES.map(field => rowData[field] || '').join('\t');
      if (action === 'copy' || action === 'cut') {
        navigator.clipboard.writeText(rowText);
      }
      if (action === 'cut' || action === 'delete') {
        setFormState(prev => {
          const newGridData = [...prev.gridData];
          newGridData[index] = createEmptyRow(newGridData[index].id);
          return { ...prev, gridData: newGridData };
        });
      }
    } else if (type === 'col') {
      const colData = gridData.map(row => row[FIELD_NAMES[index]] || '');
      const colText = colData.join('\n');
      if (action === 'copy' || action === 'cut') {
        navigator.clipboard.writeText(colText);
      }
      if (action === 'cut' || action === 'delete') {
        setFormState(prev => {
          const newGridData = prev.gridData.map(row => {
            const newRow = { ...row };
            newRow[FIELD_NAMES[index]] = '';
            return newRow;
          });
          return { ...prev, gridData: newGridData };
        });
      }
    }
    setContextMenu(null);
  };

  const ContextMenu = () => {
    if (!contextMenu) return null;
    return (
      <div id="context-menu-sales" style={{ top: contextMenu.y, left: contextMenu.x }} className="fixed z-50">
        <div className="bg-slate-900 border border-slate-700 rounded-md shadow-lg p-1 text-white text-sm">
          <button onClick={() => handleContextAction('copy')} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-slate-800 rounded-sm"><Clipboard className="w-4 h-4" /> Copiar</button>
          <button onClick={() => handleContextAction('cut')} className="flex items-center gap-2 w-full text-left px-3 py-1.5 hover:bg-slate-800 rounded-sm"><Scissors className="w-4 h-4" /> Recortar</button>
          <button onClick={() => handleContextAction('delete')} className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-red-400 hover:bg-slate-800 rounded-sm"><Trash2 className="w-4 h-4" /> Deletar</button>
        </div>
      </div>
    );
  };


  return (
    <Dialog open={isOpen} onOpenChange={isSaving ? () => { } : onClose}>
      <DialogContent className="max-w-fit" style={{ maxWidth: step === 1 ? '400px' : '90vw' }}>
        {step === 1 && (
          <div key="step1">
            <DialogHeader><DialogTitle className="text-xl">Selecionar Operadora</DialogTitle></DialogHeader>
            <div className="py-6 space-y-4">
              <Label>Para qual operadora você deseja lançar as vendas?</Label>
              <Select onValueChange={(value) => setFormState(prev => ({ ...prev, selectedOperator: Number(value), step: 2 }))}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (<SelectItem key={op.id} value={op.id.toString()}>{op.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div key="step2" className="h-[80vh] flex flex-col relative">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl flex items-center gap-2">
                  <UploadCloud className="w-6 h-6 text-green-400" />
                  Lançar Vendas para: <span className="text-green-400">{getOperatorName()}</span>
                </DialogTitle>

                {/* Botão de Importar Arquivo */}
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xls,.xlsx"
                    onChange={handleFileImport}
                    className="hidden"
                    disabled={isSaving || isImporting}
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isSaving || isImporting}
                    variant="outline"
                    className="gap-2 border-green-500/50 hover:border-green-500 hover:bg-green-500/10"
                  >
                    {isImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Importando...
                      </>
                    ) : (
                      <>
                        <FileUp className="w-4 h-4" />
                        Importar Extrato
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogHeader>
            <p className="text-sm text-white/60 mt-2">Copie os dados da sua planilha (use TAB para separar colunas) e cole na célula desejada.</p>

            {hasNegativeValues && (
              <div className="mt-4 bg-red-500/10 border border-red-500/50 text-red-500 px-4 py-3 rounded-lg flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>
                    <p className="font-bold">ATENÇÃO: Valores negativos detectados na coluna de valor.</p>
                    <p className="text-sm text-red-400">Linhas destacadas em vermelho podem representar estornos. Verifique antes de salvar para não afetar suas vendas.</p>
                  </div>
                </div>
                <Button 
                  onClick={scrollToNextNegative}
                  variant="outline"
                  className="bg-red-500/20 hover:bg-red-500/40 text-red-100 border-red-500/50 flex-shrink-0 gap-2"
                >
                  ⬇️ Ir para valor negativo
                </Button>
              </div>
            )}

            <div ref={gridRef} className="flex-grow mt-4 overflow-auto border border-slate-700 rounded-lg">
              <div className="grid grid-container" style={{ gridTemplateColumns: 'minmax(50px, auto) repeat(7, minmax(150px, 1fr))' }}>
                <div className="p-2 bg-slate-900 border-b border-r border-slate-700 sticky top-0 z-20"></div>
                {LABELS.map((label, i) => {
                  const isSelectedCol = contextMenu?.type === 'col' && contextMenu?.index === i;
                  return (
                  <div 
                    key={label} 
                    onContextMenu={(e) => handleContextMenu(e, 'col', i)} 
                    className={`p-2 bg-slate-800 text-center font-bold border-b border-r border-slate-700 sticky top-0 z-20 cursor-pointer hover:bg-slate-700 ${isSelectedCol ? 'bg-blue-500/30' : ''}`}
                  >
                    {label}
                  </div>
                  );
                })}

                {gridData.map((row, rowIndex) => {
                  const isNegativeRow = String(row.valorcompra || '').trim().startsWith('-');
                  const isSelectedRow = contextMenu?.type === 'row' && contextMenu?.index === rowIndex;
                  
                  return (
                  <React.Fragment key={row.id}>
                    <div 
                      onContextMenu={(e) => handleContextMenu(e, 'row', rowIndex)} 
                      className={`p-2 bg-slate-900 text-center border-b border-r border-slate-700 sticky left-0 cursor-pointer hover:bg-slate-700 ${isNegativeRow ? 'text-red-500 font-bold' : 'text-white/60'} ${isSelectedRow ? 'bg-blue-500/20' : ''}`}
                    >
                      {rowIndex + 1}
                    </div>
                    {FIELD_NAMES.map((field, colIndex) => {
                      const isSelectedCol = contextMenu?.type === 'col' && contextMenu?.index === colIndex;
                      const isHighlighted = isSelectedRow || isSelectedCol;
                      
                      return (
                      <div key={field} className={`grid-cell border-b border-r border-slate-700 ${isHighlighted ? 'bg-blue-500/10' : ''}`}>
                        <textarea
                          value={row[field] || ''}
                          onPaste={(e) => handlePaste(e, rowIndex, colIndex)}
                          onChange={(e) => handleGridChange(rowIndex, colIndex, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                          className={`bg-transparent p-2 w-full h-full box-border resize-none overflow-hidden focus:bg-slate-700/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:z-10 ${isNegativeRow ? 'text-red-500 font-bold' : ''}`}
                          rows="1"
                          disabled={isSaving}
                          data-row={rowIndex}
                          data-col={colIndex}
                        />
                      </div>
                      );
                    })}
                  </React.Fragment>
                  );
                })}
              </div>
            </div>
            <ContextMenu />

            <div className="mt-4 flex justify-between items-center pt-4 border-t border-white/10 gap-4">
              <Button variant="ghost" onClick={() => setFormState(prev => ({ ...prev, step: 1 }))} disabled={isSaving}>Voltar</Button>
              <div>
                <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancelar</Button>
                <Button onClick={handleSave} className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 ml-2" disabled={isSaving}>
                  {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  {isSaving ? 'Gravando...' : 'Salvar Vendas'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      {/* Loading Progress Overlay */}
      <LoadingProgress
        isVisible={isSaving}
        message="Aguarde, suas informações estão sendo gravadas..."
        progress={saveProgress}
        showProgress={true}
      />
    </Dialog>
  );
};

export default PasteSalesForm;